import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { mergeVertices, toCreasedNormals } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { POSES, type PoseId } from '@/content/articulation';
import { TONGUE_DORSUM_REST } from '@/content/anatomy.gen';
import { JAW_PIVOT } from '@/content/articulation3d';
import { cavityOutline, oralCavity } from '@/content/airway';
import { joinedOutline, midlineLoops } from './midline';
import {
  applyStencil,
  loopStencil,
  neighbours,
  taubinSmooth,
  type Neighbours,
  type Stencil,
} from './subdivide';
import { palateSection } from '@/content/palate';
import { bindTongue, poseTongue, type TongueBinding } from '@/content/tongueRig';
import {
  FLOOR_OF_MOUTH,
  TONGUE_TARGETS,
  blendTarget,
  roofHeight,
  type TongueTarget,
} from '@/content/tongueTargets';

/**
 * The real-time 3D mouth (issue #25), as a stage that lessons direct: a child's head as see-through
 * skin, the measured roof of the mouth and throat wall, the teeth, and the anatomical tongue (always
 * whole) posed onto each sound's target (tongueTargets.ts, built to
 * docs/design/articulation-spec.md), lips and jaw moving with it in real time. Lessons move the
 * mouth between any shapes (`moveTo`, `setBlend`, `setShape`), choose camera shots (`setShot`),
 * and read where things are (`gap`, `toScreen`, `fromScreen`) for interactions such as dragging
 * the tongue. The teeth, roof and throat fade on the camera's side instead of being cut away: they
 * stay as references for where the tongue is.
 *
 * Coordinates: millimetres in the anatomy (origin at the upper incisors' edge, y up, z forward,
 * x to the speaker's left), metres in the scene.
 */

const MM = 0.001;

const COLORS = {
  skin: 0xf2b996,
  roof: 0xc9707e,
  roofCut: 0x8f3e4c,
  tongue: 0xd9606f,
  teeth: 0xf7f3ea,
  throat: 0xd08690,
  gums: 0xe7919a,
  softPalate: 0xb9636f,
  cutFace: 0xf6cbbd,
  floor: 0xc9858c,
  highlight: 0xffd23f,
  touch: 0xff3b30,
};

/** A shape of the mouth: the tongue's target and the jaw and lips. */
export interface MouthShape {
  tongue: TongueTarget;
  jaw: number;
  lips: { height: number; width: number; protrusion: number };
}

export function shapeOf(pose: PoseId): MouthShape {
  const p = POSES[pose];
  return { tongue: TONGUE_TARGETS[pose], jaw: p.jaw, lips: { ...p.lips } };
}

export function blendShapes(a: MouthShape, b: MouthShape, t: number): MouthShape {
  const mix = (x: number, y: number) => x + (y - x) * t;
  return {
    tongue: blendTarget(a.tongue, b.tongue, t),
    jaw: mix(a.jaw, b.jaw),
    lips: {
      height: mix(a.lips.height, b.lips.height),
      width: mix(a.lips.width, b.lips.width),
      protrusion: mix(a.lips.protrusion, b.lips.protrusion),
    },
  };
}

/**
 * The narrowest gap (mm) between the tongue's midline and the roof of the mouth, and where (z).
 * Below about 1 mm the tongue touches the roof.
 */
export function roofGap(tongue: TongueTarget): { mm: number; z: number } {
  let best = { mm: Infinity, z: 0 };
  for (const [z, y] of tongue.dorsum) {
    const roof = roofHeight(z);
    if (roof === undefined) continue;
    const mm = roof - y;
    if (mm < best.mm) best = { mm, z };
  }
  return best;
}

export type ShotId = 'side' | 'threeQuarter' | 'front';

interface Shot {
  /** Angle from the speaker's right side toward the front (radians). */
  azimuth: number;
  /** Angle up from level (radians). */
  elevation: number;
  distance: number;
  /** Where the camera looks (mm). */
  focus: readonly [number, number, number];
  /** How visible the see-through face is (the front view looks through it). */
  skin: number;
  /** How visible the roof of the mouth is (the view from above looks past it). */
  palate: number;
  /** Opacity of the teeth, roof and throat on the camera's side of the midline. */
  near: number;
}

export const SHOTS: Readonly<Record<ShotId, Shot>> = {
  side: {
    azimuth: 0.12,
    elevation: 0.04,
    distance: 0.2,
    focus: [0, -4, -36],
    skin: 1,
    palate: 1,
    near: 0.4,
  },
  threeQuarter: {
    azimuth: 0.55,
    elevation: 0.22,
    distance: 0.21,
    focus: [0, -2, -38],
    skin: 1,
    palate: 1,
    near: 0.4,
  },
  front: {
    azimuth: Math.PI / 2,
    elevation: 0.55,
    distance: 0.12,
    focus: [0, 6, -38],
    skin: 0.5,
    palate: 0.18,
    // The near teeth mostly solid from the front: at 0.6 the pink lining behind them showed
    // through, which looked like stains on the teeth.
    near: 0.8,
  },
};

const ease = (k: number) => k * k * (3 - 2 * k);

/** The stage shape (width / height) the shots are framed for (the model preview's 4:3). */
const FRAMED_ASPECT = 4 / 3;

/** The tongue is see-through below the first height and solid above the second (mm). */
const TONGUE_FADE = [-27, -17] as const;

/**
 * Smooth shading for the anatomy meshes: the exported glb has no normals, so three.js would shade
 * each triangle flat (teeth and tissue looked faceted, the muscles like fibres).
 */
function smooth(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  // A copy: the loaded geometry is shared by every model on the page.
  const copy = geometry.clone();
  copy.deleteAttribute('normal');
  const merged = mergeVertices(copy, 1e-5);
  merged.computeVertexNormals();
  return merged;
}

/**
 * The teeth's shading: smooth across their curved faces, but sharp where the surface folds by
 * more than TEETH_CREASE (the incisal edges and cusps), which smoothing everywhere had rounded
 * into blunt, "monster"-like edges (owner, 2026-10-14).
 */
const TEETH_CREASE = (35 * Math.PI) / 180;

function sharpEdges(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const copy = geometry.clone();
  copy.deleteAttribute('normal');
  return toCreasedNormals(mergeVertices(copy, 1e-5), TEETH_CREASE);
}

interface TongueAsset {
  /** The measured mesh, which the rig poses (its smoothing is tuned to this mesh). */
  vertices: Float32Array;
  faces: Uint32Array;
  binding: TongueBinding;
  /** One step of Loop subdivision of the posed mesh (fixed weights): what is drawn. */
  fine: Stencil;
  /** The drawn mesh's neighbours, for smoothing it. */
  neighbours: Neighbours;
  /** Tissue colour per drawn vertex (RGB factors): lighter on top, deeper red at edges, below. */
  tint: Float32Array;
}

let tongueAsset: Promise<TongueAsset> | undefined;
function loadTongue(): Promise<TongueAsset> {
  tongueAsset ??= fetch(`${import.meta.env.BASE_URL}articulator/tongue.json`)
    .then((r) => r.json() as Promise<{ vertices: number[][]; faces: number[][] }>)
    .then((data) => {
      const vertices = Float32Array.from(data.vertices.flat());
      const faces = Uint32Array.from(data.faces.flat());
      // Drawn subdivided (about 34k triangles): the measured mesh's facets showed at the
      // silhouette and where it bends (owner, 2026-10-14: "a bit low poly"). The rig poses the
      // measured mesh, then the posed shape is subdivided each frame.
      const fine = loopStencil(vertices.length / 3, faces);
      const restFine = new Float32Array((fine.start.length - 1) * 3);
      applyStencil(fine, vertices, restFine);
      return {
        vertices,
        faces,
        binding: bindTongue(vertices, TONGUE_DORSUM_REST),
        fine,
        neighbours: neighbours(restFine.length / 3, fine.faces),
        tint: tongueTint(restFine, fine.faces),
      };
    });
  return tongueAsset;
}

/**
 * Tissue colour factors per vertex, from the rest shape's normals: the upper surface a lighter
 * pink, the edges and underside a deeper, slightly bluer red (as real tongues and the reference
 * renders look), blended smoothly.
 */
function tongueTint(vertices: Float32Array, faces: Uint32Array): Float32Array {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
  geometry.setIndex(new THREE.BufferAttribute(faces, 1));
  geometry.computeVertexNormals();
  const normals = geometry.getAttribute('normal');
  const tint = new Float32Array(vertices.length);
  for (let v = 0; v < vertices.length / 3; v++) {
    const up = normals.getY(v);
    const t = Math.min(1, Math.max(0, (up + 0.15) / 0.75));
    const k = t * t * (3 - 2 * t);
    tint.set([0.8 + 0.25 * k, 0.66 + 0.36 * k, 0.72 + 0.32 * k], v * 3);
  }
  geometry.dispose();
  return tint;
}

let headAsset: Promise<THREE.Mesh[]> | undefined;
function loadHead(): Promise<THREE.Mesh[]> {
  headAsset ??= new GLTFLoader()
    .loadAsync(`${import.meta.env.BASE_URL}articulator/head.glb`)
    .then((gltf) => {
      const meshes: THREE.Mesh[] = [];
      gltf.scene.traverse((o) => {
        if (o instanceof THREE.Mesh) meshes.push(o as THREE.Mesh);
      });
      return meshes;
    });
  return headAsset;
}

let mouthAsset: Promise<THREE.Group> | undefined;
function loadMouth(): Promise<THREE.Group> {
  mouthAsset ??= new GLTFLoader()
    .loadAsync(`${import.meta.env.BASE_URL}articulator/mouth.glb`)
    .then((gltf) => gltf.scene);
  return mouthAsset;
}

/**
 * Mouth shapes for the head's morphs: the jaw opening drives "open"; rounded, pushed-out lips (/w/)
 * drive "purse" and "protrude"; a wider opening (/r/) parts the lips a little.
 */
/** How far (mm) the head's "purse" and "protrude" shapes move the lips forward at full weight. */
const PURSE_MM = 15;
const PROTRUDE_MM = 10.6;

function mouthWeights(shape: MouthShape): Record<string, number> {
  const round = Math.max(0, Math.min(1, (36 - shape.lips.width) / 24));
  // Rounding narrows the lips with a little forward movement; "protrude" brings the total to the
  // pose's protrusion in mm (the shapes' measured reach: PURSE_MM, PROTRUDE_MM at full weight).
  // Stacked at full strength they pushed the lips about 18 mm forward for /w/; real lip
  // protrusion for /w/ is about 5-7 mm.
  const purse = 0.35 * round;
  const protrude = Math.max(
    0,
    Math.min(1, (shape.lips.protrusion - purse * PURSE_MM) / PROTRUDE_MM),
  );
  return {
    // Lips wider than at rest (36 mm), as for "ee", spread toward a smile.
    spread: 0.7 * Math.max(0, Math.min(1, (shape.lips.width - 36) / 12)),
    open: Math.max(0, Math.min(1, (shape.jaw - 1.5) / 7)),
    purse,
    protrude,
    part: 0.6 * Math.max(0, Math.min(1, (shape.lips.height - 4) / 8)),
  };
}

/**
 * Soft, moist tissue: a matte base with a thin wet clear coat and a soft sheen at grazing angles,
 * and papillae from a texture (tongue-papillae.png, made from a generated tile by
 * ml/experiments/tongue_texture.py) as a bump and a light colour variation. Vertex colours carry the tissue
 * tint (RGB) and the root's fade (alpha). The coat is rough enough that the bent sides catch no
 * hard streak of light (blind review, 2026-10-14: a shine streak read as a scratch).
 */
/**
 * Fitting the face (head.glb) to the teeth and jaws (mouth.glb), which come from different
 * models (mm): the face moves back so the upper lip rests on the upper incisors (its inner
 * surface stood 6-7 mm in front of them), and the lower teeth and gums move forward so they sit
 * 2-3 mm behind the upper ones, a normal overjet (they were 4.8 mm behind).
 */
const HEAD_BACK_MM = 5.5;
const LOWER_FORWARD_MM = 2;

/** The papillae texture's tile size and the bumps' height (mm). */
const PAPILLAE_TILE_MM = 30;
const PAPILLAE_HEIGHT_MM = 0.4;

/** Taubin passes over the drawn (subdivided) tongue. */
const TONGUE_SMOOTHING_PASSES = 3;

function tongueMaterial(): THREE.MeshPhysicalMaterial {
  const material = new THREE.MeshPhysicalMaterial({
    color: COLORS.tongue,
    roughness: 0.68,
    clearcoat: 0.35,
    clearcoatRoughness: 0.45,
    sheen: 0.5,
    sheenRoughness: 0.6,
    sheenColor: new THREE.Color(0xffb3bc),
    vertexColors: true,
    transparent: true,
  });
  const papillae = new THREE.TextureLoader().load(
    `${import.meta.env.BASE_URL}articulator/tongue-papillae.png`,
  );
  papillae.wrapS = papillae.wrapT = THREE.RepeatWrapping;
  papillae.colorSpace = THREE.NoColorSpace;
  material.onBeforeCompile = (shader) => {
    shader.uniforms.papillae = { value: papillae };
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
varying vec3 vTonguePos;
varying vec3 vTongueNormal;`,
      )
      .replace(
        '#include <beginnormal_vertex>',
        `#include <beginnormal_vertex>
vTongueNormal = objectNormal;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
vTonguePos = transformed;`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform sampler2D papillae;
varying vec3 vTonguePos;
varying vec3 vTongueNormal;
// The papillae tile (${String(PAPILLAE_TILE_MM)} mm across) projected from three sides, blended by
// which way the surface faces: the tongue mesh has no texture coordinates.
float papillaHeight() {
  vec3 w = pow(abs(normalize(vTongueNormal)), vec3(4.0));
  w /= w.x + w.y + w.z;
  vec3 p = vTonguePos / ${PAPILLAE_TILE_MM.toFixed(1)};
  return texture2D(papillae, p.zy).r * w.x + texture2D(papillae, p.xz).r * w.y
    + texture2D(papillae, p.xy).r * w.z;
}`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
float papilla = papillaHeight();
// Tips a touch lighter, the furrows between them a touch deeper red.
diffuseColor.rgb *= mix(0.8, 1.1, papilla);`,
      )
      .replace(
        '#include <normal_fragment_maps>',
        `#include <normal_fragment_maps>
{
  // A bump from the papillae's height (as three.js bump maps do): its screen-space slope tilts
  // the normal. Height range about ${String(PAPILLAE_HEIGHT_MM)} mm.
  vec2 dHdxy = vec2(dFdx(papilla), dFdy(papilla)) * ${(PAPILLAE_HEIGHT_MM / 1000).toFixed(5)};
  vec3 sigmaX = dFdx(-vViewPosition);
  vec3 sigmaY = dFdy(-vViewPosition);
  vec3 r1 = cross(sigmaY, normal);
  vec3 r2 = cross(normal, sigmaX);
  float det = dot(sigmaX, r1) * faceDirection;
  vec3 grad = sign(det) * (dHdxy.x * r1 + dHdxy.y * r2);
  normal = normalize(abs(det) * normal - grad);
}`,
      );
  };
  return material;
}

/**
 * Where the head fades out (mm, the head model's frame): eyes without pupils and a bald scalp
 * unsettled young children (owner, 2026-10-14), so only the nose, mouth, jaw and ears show. The
 * front of the face fades just under the eyes (the eyes sit at about 55-65 mm, the nose tip at
 * 30 mm), in front of the ears only (they lie 75-120 mm back and reach 110 mm up); everything
 * fades above the ears.
 */
const HEAD_FADE = { eyes: [46, 54], front: [-72, -58], top: [108, 122] } as const;

/**
 * The see-through skin: `face` opacity where it faces the viewer, rising toward the silhouette;
 * drawn only where it is the nearest surface (after a depth-only pass of the same mesh). Fades out
 * over the eyes and the scalp (HEAD_FADE).
 */
function xraySkin(face: number, rim = 0.75): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    color: COLORS.skin,
    roughness: 0.6,
    transparent: true,
    depthWrite: false,
    depthFunc: THREE.LessEqualDepth,
  });
  const [e0, e1] = HEAD_FADE.eyes;
  const [f0, f1] = HEAD_FADE.front;
  const [t0, t1] = HEAD_FADE.top;
  const n = (v: number) => (v / 1000).toFixed(4);
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\nvarying vec3 vHeadPos;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\nvHeadPos = transformed;`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nvarying vec3 vHeadPos;`)
      .replace(
        '#include <opaque_fragment>',
        `#include <opaque_fragment>
      float facing = abs(dot(normalize(vViewPosition), normal));
      gl_FragColor.a *= mix(${rim.toFixed(3)}, ${face.toFixed(3)}, pow(facing, 1.6));
      float eyes = smoothstep(${n(e0)}, ${n(e1)}, vHeadPos.y) * smoothstep(${n(f0)}, ${n(f1)}, vHeadPos.z);
      float scalp = smoothstep(${n(t0)}, ${n(t1)}, vHeadPos.y);
      gl_FragColor.a *= 1.0 - max(eyes, scalp);`,
      );
  };
  return material;
}

/**
 * A surface whose half toward the camera (the speaker's right, x < 0) fades to `near.value`
 * opacity, gradually across the midline: it stays as a reference (the teeth show where the tongue
 * is) without hiding what is behind it.
 */
function fadeNear(
  color: number,
  near: { value: number },
  side: THREE.Side = THREE.FrontSide,
  vertexColors = false,
) {
  const material = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.45,
    side,
    transparent: true,
    vertexColors,
  });
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uNear = near;
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'varying float vFadeX;\nvoid main() {')
      .replace(
        '#include <project_vertex>',
        '#include <project_vertex>\n  vFadeX = (modelMatrix * vec4(transformed, 1.0)).x;',
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', 'varying float vFadeX;\nuniform float uNear;\nvoid main() {')
      .replace(
        '#include <opaque_fragment>',
        '#include <opaque_fragment>\n  gl_FragColor.a *= mix(uNear, 1.0, smoothstep(-0.006, 0.002, vFadeX));',
      );
  };
  return material;
}

/**
 * Teeth seated in gums: each crown blends from white to gum pink over its last 2.5 mm toward the
 * gum (upward for the upper teeth, downward for the lower), found per 2 mm depth slice.
 */
function gumLine(geometry: THREE.BufferGeometry, upper: boolean): THREE.BufferGeometry {
  const position = geometry.getAttribute('position');
  const edge = new Map<number, number>();
  for (let v = 0; v < position.count; v++) {
    const key = Math.round(position.getZ(v) / 0.002);
    const y = position.getY(v);
    const e = edge.get(key);
    edge.set(key, e === undefined ? y : upper ? Math.max(e, y) : Math.min(e, y));
  }
  const gum = new THREE.Color(COLORS.gums);
  const tooth = new THREE.Color(1, 1, 1);
  const colors = new Float32Array(position.count * 4);
  const c = new THREE.Color();
  for (let v = 0; v < position.count; v++) {
    const e = edge.get(Math.round(position.getZ(v) / 0.002)) ?? 0;
    // A faint, narrow blend (1 mm) where the crown meets the gum band: a wider, stronger pink
    // (2.5 mm) looked like stains on the teeth (owner, 2026-10-14); the gums are drawn themselves.
    const k = Math.min(1, Math.max(0, 1 - Math.abs(e - position.getY(v)) / 0.001));
    c.copy(tooth).lerp(
      gum.clone().multiply(new THREE.Color(1 / 0.97, 1 / 0.95, 1 / 0.92)),
      0.35 * k,
    );
    colors.set([c.r, c.g, c.b, 1], v * 4);
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 4));
  return geometry;
}

/**
 * A see-through mesh drawn as its nearest surface only: a depth-only pass first, then the colour
 * pass where it is the nearest (order: `order` and `order + 1`). Without it a translucent tooth
 * shows its own back and the teeth behind it through itself (pale blobs, broken-looking rows).
 */
function nearestOnly(mesh: THREE.Mesh, order: number): THREE.Group {
  const depth = new THREE.Mesh(
    mesh.geometry,
    new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: true, transparent: true }),
  );
  depth.renderOrder = order;
  const material = mesh.material as THREE.MeshStandardMaterial;
  material.depthWrite = false;
  material.depthFunc = THREE.LessEqualDepth;
  mesh.renderOrder = order + 1;
  const group = new THREE.Group();
  group.add(depth, mesh);
  return group;
}

/** A thin surface with both sides shaded (outside lighter), fading on the camera's side. */
function fadeSurface(
  geometry: THREE.BufferGeometry,
  outside: number,
  inside: number,
  near: { value: number },
  vertexColors = false,
): THREE.Group {
  const group = new THREE.Group();
  group.add(
    new THREE.Mesh(geometry, fadeNear(outside, near, THREE.FrontSide, vertexColors)),
    new THREE.Mesh(geometry, fadeNear(inside, near, THREE.BackSide, vertexColors)),
  );
  return group;
}

export interface TongueSceneOptions {
  /** Called when the shot changes (for captions). */
  onShot?: (shot: ShotId) => void;
  /** Let the viewer turn the model by dragging (off while a lesson drags the tongue). */
  orbitControls?: boolean;
  /**
   * 'xray': see-through skin; 'cutaway': the head cut at the midline, the cut face filled, the
   * mouth open in it (as speech-therapy diagrams draw it), the tongue whole inside.
   */
  style?: 'xray' | 'cutaway';
  /** Keep the last drawn frame readable (for recording it with captions drawn over it). */
  preserveDrawingBuffer?: boolean;
}

/** The real-time mouth model on a host element. */
export class TongueScene {
  readonly ready: Promise<void>;
  private readonly host: HTMLElement;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(30, 1, 0.01, 2);
  private readonly controls: OrbitControls;
  private readonly jaw = new THREE.Group();
  private readonly upper = new THREE.Group();
  private readonly tongueMesh: THREE.Group;
  private readonly tongueGeometry = new THREE.BufferGeometry();
  private readonly glow: THREE.Mesh;
  private tongueData: TongueAsset | null = null;
  private posed = new Float32Array(0);
  private tongueFade = new Float32Array(0);
  private smoothScratch = new Float32Array(0);
  /**
   * The face's and the lower teeth's offsets (mm: [forward, up]) against the upper teeth, palate
   * and tongue, which come from one model (see HEAD_BACK_MM).
   */
  private fit: { face: [number, number]; lower: [number, number] } = {
    face: [-HEAD_BACK_MM, 0],
    lower: [LOWER_FORWARD_MM, 0],
  };
  private readonly lowerParts: THREE.Object3D[] = [];
  /** The posed tongue, subdivided: what is drawn. */
  private drawn = new Float32Array(0);
  private head: THREE.Mesh[] = [];
  /** The see-through face's layers and their full opacity. */
  private readonly skinLayers: [THREE.Mesh, number][] = [];
  private readonly palateMaterials: THREE.MeshStandardMaterial[] = [];
  /** Opacity of the teeth, roof and throat on the camera's side (per shot). */
  private readonly nearFade = { value: 0.2 };
  private readonly nearGums = { value: 0.12 };
  private readonly nearLining = { value: 0.04 };
  private shape: MouthShape = shapeOf('rest');
  private shapeTween: {
    from: MouthShape;
    to: MouthShape;
    start: number;
    ms: number;
    done: () => void;
  } | null = null;
  private shot: Shot = SHOTS.side;
  private shotId: ShotId = 'side';
  /** The camera's move: its shot at each fraction (0-1) of `ms`. */
  private shotTween: {
    at: (k: number) => Shot;
    start: number;
    ms: number;
    done: () => void;
  } | null = null;
  private userTurned = false;
  private raf = 0;
  private visible = false;
  private disposed = false;
  private readonly observer: IntersectionObserver;
  private readonly sizes: ResizeObserver;
  private readonly onShot: ((shot: ShotId) => void) | undefined;
  private readonly style: 'xray' | 'cutaway';
  /** Highlight the place where the tongue meets or nears the roof. */
  private showGap = false;

  constructor(host: HTMLElement, options: TongueSceneOptions = {}) {
    this.host = host;
    this.onShot = options.onShot;
    this.style = options.style ?? 'xray';
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: options.preserveDrawingBuffer ?? false,
    });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    // Sized by CSS to the host: its drawing buffer (device pixels) must not size the host, or the
    // host grows to fit it on every resize (a 22-million-pixel canvas at a device pixel ratio of 2).
    Object.assign(this.renderer.domElement.style, {
      display: 'block',
      width: '100%',
      height: '100%',
    });
    host.appendChild(this.renderer.domElement);
    this.renderer.localClippingEnabled = this.style === 'cutaway';

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x6b5555, 1.6));
    const key = new THREE.DirectionalLight(0xffffff, 1.5);
    key.position.set(-1, 1.2, 0.9);
    const fill = new THREE.DirectionalLight(0xffe8e0, 0.6);
    fill.position.set(1, 0.2, -1);
    this.scene.add(key, fill);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enablePan = false;
    this.controls.enableZoom = false;
    this.controls.enableDamping = true;
    this.controls.enabled = options.orbitControls ?? true;
    this.controls.addEventListener('start', () => {
      this.userTurned = true;
    });

    this.scene.add(this.upper, this.jaw);
    // The tongue is always whole: its full 3D shape (the sides, the groove, how it bunches) is what
    // the lessons show.
    this.tongueMesh = new THREE.Group();
    this.tongueMesh.add(
      new THREE.Mesh(
        this.tongueGeometry,
        // Vertex alpha: the root fades out below the floor of the mouth (TONGUE_FADE), so the
        // tongue shows as the part a child moves, not a mass hanging into the neck.
        tongueMaterial(),
      ),
    );
    // Drawn after the lining and before the gums and teeth, which blend over it.
    for (const child of this.tongueMesh.children) child.renderOrder = 1;
    this.tongueMesh.scale.setScalar(MM);
    this.upper.add(this.tongueMesh);

    // The mouth's lining (airway.ts): one surface around the cavity, the palate's vault on top,
    // the cheeks outside the teeth, the floor below; it fades out toward the back of the mouth
    // (no throat: the model stays inside the mouth).
    // The cutaway's lining reaches from inside the lips to behind the open mouth's back edge, so the
    // opening shows only the mouth.
    const cavity = this.style === 'cutaway' ? oralCavity(48, 44, 4, -79) : oralCavity();
    const cavityGeometry = new THREE.BufferGeometry();
    cavityGeometry.setAttribute('position', new THREE.BufferAttribute(cavity.positions, 3));
    const cavityShade = new Float32Array((cavity.positions.length / 3) * 4);
    for (let v = 0; v < cavity.positions.length / 3; v++) {
      const y = cavity.positions[v * 3 + 1] ?? 0;
      const z = cavity.positions[v * 3 + 2] ?? 0;
      // Fade out over the back 14 mm; the roof's soft part (behind about 54 mm) a deeper pink.
      // Fade out over the back 14 mm and the front 6 mm (no hard edges where it opens).
      // (The cutaway keeps it solid: it is the backdrop seen through the open mouth.)
      const back =
        this.style === 'cutaway'
          ? 1
          : Math.min(1, Math.max(0, (z + 70) / 14), Math.max(0, (-6 - z) / 6));
      const soft = y > 8 ? Math.min(1, Math.max(0, (-50 - z) / 8)) : 0;
      cavityShade.set(
        [1 - 0.12 * soft, 1 - 0.32 * soft, 1 - 0.25 * soft, back * back * (3 - 2 * back)],
        v * 4,
      );
    }
    cavityGeometry.setAttribute('color', new THREE.BufferAttribute(cavityShade, 4));
    cavityGeometry.setIndex(cavity.index);
    cavityGeometry.computeVertexNormals();
    const lining = fadeSurface(cavityGeometry, COLORS.roof, COLORS.roof, this.nearLining, true);
    lining.scale.setScalar(MM);
    this.upper.add(lining);
    for (const child of lining.children as THREE.Mesh[]) {
      const material = child.material as THREE.MeshStandardMaterial;
      // The lining never hides what is inside the mouth: its faint near half is drawn before the
      // tongue, and writing depth there would hide the tongue behind it.
      material.depthWrite = false;
      this.palateMaterials.push(material);
    }

    // Where the tongue nears or touches the roof: a soft dot, shown by interactions.
    this.glow = new THREE.Mesh(
      new THREE.SphereGeometry(2.2 * MM, 16, 12),
      new THREE.MeshBasicMaterial({
        color: COLORS.highlight,
        transparent: true,
        opacity: 0.85,
        depthTest: false,
      }),
    );
    this.glow.renderOrder = 10;
    this.glow.visible = false;
    this.scene.add(this.glow);

    this.ready = Promise.all([
      loadTongue().then((data) => {
        this.tongueData = data;
        this.posed = new Float32Array(data.vertices.length);
        this.drawn = new Float32Array(data.tint.length);
        this.tongueGeometry.setAttribute('position', new THREE.BufferAttribute(this.drawn, 3));
        this.tongueGeometry.setIndex(new THREE.BufferAttribute(data.fine.faces, 1));
        this.tongueFade = new Float32Array((data.tint.length / 3) * 4).fill(1);
        for (let v = 0; v < data.tint.length / 3; v++)
          this.tongueFade.set(data.tint.subarray(v * 3, v * 3 + 3), v * 4);
        this.smoothScratch = new Float32Array(data.tint.length);
        this.tongueGeometry.setAttribute('color', new THREE.BufferAttribute(this.tongueFade, 4));
      }),
      // The head is see-through skin, not cut: its outline places the mouth in a face, while
      // the inside stays clear (a cut-open head shows a hollow shell).
      loadHead().then(([skin, lips]) => {
        if (!skin || !lips || this.disposed) return;
        if (this.style === 'cutaway') {
          this.cutawayHead(skin, lips);
          return;
        }
        // X-ray skin: nearly clear where the view goes straight through, stronger toward the
        // silhouette (as medical animations show a body), and only the nearest surface drawn: a
        // depth-only pass first, so the inside of the cheeks and ears never shows through.
        const morph = (mesh: THREE.Mesh) => {
          mesh.morphTargetDictionary = skin.morphTargetDictionary;
          mesh.morphTargetInfluences = [...(skin.morphTargetInfluences ?? [])];
          mesh.position.set(0, this.fit.face[1] * MM, this.fit.face[0] * MM);
          this.head.push(mesh);
          this.scene.add(mesh);
        };
        for (const part of [skin, lips]) {
          const depth = new THREE.Mesh(
            part.geometry,
            // In the see-through queue (after the faded teeth and roof), so it hides only
            // what lies behind the skin's own far surfaces.
            new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: true, transparent: true }),
          );
          depth.renderOrder = 7;
          morph(depth);
          // The lips' inner edge meets the view at a grazing angle: a weak rim there, or it shows
          // as a ragged bright border.
          const mesh = new THREE.Mesh(
            part.geometry,
            part === lips ? xraySkin(0.22, 0.35) : xraySkin(0.1),
          );
          mesh.renderOrder = 8;
          this.skinLayers.push([mesh, 1]);
          morph(mesh);
        }
      }),
      loadMouth().then((mouth) => {
        if (this.disposed) return;
        mouth.clone().traverse((o) => {
          if (!(o instanceof THREE.Mesh)) return;
          // The ridges holding the teeth, both sides (the near side see-through like its teeth: a
          // jaw shown cut in half looked creepy), shaped by the jaws' bone but shown as the pink gum
          // that covers it (bone looked like a skeleton: too creepy for children), so the teeth sit
          // in gums instead of floating.
          if (o.name.startsWith('mandible') || o.name.startsWith('maxilla')) {
            const geometry = smooth(o.geometry as THREE.BufferGeometry);
            const position = geometry.getAttribute('position');
            const rgba = new Float32Array(position.count * 4);
            for (let v = 0; v < position.count; v++) {
              const y = position.getY(v) / MM;
              const z = position.getZ(v) / MM;
              // The upper jaw: only the ridge holding the teeth and the palate (below about 24 mm,
              // in front of about 56 mm back), not its processes toward the cheek and skull.
              // Gums: a band around the teeth only (above the lower teeth, below the upper ones),
              // not the whole bone (it reaches down to the chin and up toward the cheek).
              const band = (from: number, to: number) =>
                Math.min(1, Math.max(0, (y - from) / (to - from)));
              const k = o.name.startsWith('maxilla')
                ? (1 - band(12, 18)) *
                  Math.min(1, Math.max(0, (z + 58) / 8)) *
                  // Gone by 12 mm back: in front of that the bone over the incisor roots is thin
                  // and broke into shards above the front teeth (the crowns' own pink gum line
                  // seats them).
                  Math.min(1, Math.max(0, (-12 - z) / 6))
                : band(-21, -13) * (1 - band(0, 6));
              rgba.set([1, 1, 1, k * k * (3 - 2 * k)], v * 4);
            }
            geometry.setAttribute('color', new THREE.BufferAttribute(rgba, 4));
            const bone = new THREE.Mesh(
              geometry,
              fadeNear(COLORS.gums, this.nearGums, THREE.FrontSide, true),
            );
            // After the teeth: the bone's depth covers its faded-out parts too, which drawn first
            // hid the teeth behind them (shards of the upper front teeth were left).
            const gums = nearestOnly(bone, 4);
            if (o.name.startsWith('mandible')) this.lowerParts.push(gums);
            (o.name.startsWith('mandible') ? this.jaw : this.upper).add(gums);
            return;
          }
          if (!o.name.includes('Teeth')) return;
          const mesh = new THREE.Mesh(
            gumLine(sharpEdges(o.geometry as THREE.BufferGeometry), o.name.startsWith('upper')),
            fadeNear(COLORS.teeth, this.nearFade, THREE.FrontSide, true),
          );
          const teeth = nearestOnly(mesh, 2);
          if (o.name.startsWith('lower')) this.lowerParts.push(teeth);
          (o.name.startsWith('lower') ? this.jaw : this.upper).add(teeth);
          this.setFit({});
        });
      }),
    ]).then(() => undefined);

    this.applyShot(SHOTS.side);
    this.observer = new IntersectionObserver(([entry]) => {
      const now = entry?.isIntersecting ?? false;
      if (now && !this.visible) this.raf = requestAnimationFrame(this.frame);
      this.visible = now;
    });
    this.observer.observe(host);
    this.sizes = new ResizeObserver(() => {
      this.resize();
    });
    this.sizes.observe(host);
    this.resize();
  }

  /**
   * The cutaway head: the far half of the skin (opaque, clipped at the midline) and the cut face,
   * filled in flesh colour with the mouth cavity left open.
   */
  private cutawayHead(skin: THREE.Mesh, lips: THREE.Mesh) {
    const keepFar = [new THREE.Plane(new THREE.Vector3(1, 0, 0), 0)];
    for (const part of [skin, lips]) {
      const mesh = new THREE.Mesh(
        part.geometry,
        new THREE.MeshStandardMaterial({
          color: COLORS.skin,
          roughness: 0.6,
          side: THREE.DoubleSide,
          clippingPlanes: keepFar,
        }),
      );
      mesh.morphTargetDictionary = skin.morphTargetDictionary;
      mesh.morphTargetInfluences = [...(skin.morphTargetInfluences ?? [])];
      mesh.position.set(0, this.fit.face[1] * MM, this.fit.face[0] * MM);
      this.head.push(mesh);
      this.scene.add(mesh);
    }
    // The head's outline at the midline: the skin's and lips' cut chains joined into one loop
    // around the mouth (the mesh is open at the neck and the lips).
    const outline = joinedOutline(
      [...midlineLoops(skin.geometry), ...midlineLoops(lips.geometry)],
      [-0.03, 0],
    );
    if (outline.length < 3) return;
    const shape = new THREE.Shape(outline.map(([z, y]) => new THREE.Vector2(z * 1000, y * 1000)));
    shape.holes.push(new THREE.Path(cavityOutline().map(([z, y]) => new THREE.Vector2(z, y))));
    const face = new THREE.Mesh(
      new THREE.ShapeGeometry(shape, 24),
      // Flat, unlit, as diagrams draw a cut face.
      new THREE.MeshBasicMaterial({ color: COLORS.cutFace, side: THREE.DoubleSide }),
    );
    // The shape's x is depth (z) and its y height: turned into the midline plane, just in front
    // of it so it never flickers against the far half.
    face.rotation.y = -Math.PI / 2;
    face.position.x = -0.0003;
    face.scale.setScalar(MM);
    this.scene.add(face);
  }

  /** The current mouth shape (for lessons that measure it). */
  get current(): MouthShape {
    return this.shape;
  }

  get currentShot(): ShotId {
    return this.shotId;
  }

  /** Shows a shape at once (e.g. while the child drags). */
  setShape(shape: MouthShape): void {
    this.shapeTween?.done();
    this.shapeTween = null;
    this.shape = shape;
  }

  /** Shows the blend of two poses at once: 0 is `from`, 1 is `to` (beyond 1 goes further). */
  setBlend(from: PoseId, to: PoseId, t: number): void {
    this.setShape(blendShapes(shapeOf(from), shapeOf(to), t));
  }

  /** Moves smoothly from the current shape to `to` (a pose or a shape) over `ms`. */
  moveTo(to: PoseId | MouthShape, ms = 900): Promise<void> {
    this.shapeTween?.done();
    const target = typeof to === 'string' ? shapeOf(to) : to;
    return new Promise((resolve) => {
      this.shapeTween = {
        from: this.shape,
        to: target,
        start: performance.now(),
        ms,
        done: resolve,
      };
    });
  }

  /** Moves the camera to a shot over `ms` (the viewer's own turning is let go). */
  setShot(id: ShotId, ms = 1400): Promise<void> {
    this.shotTween?.done();
    this.userTurned = false;
    this.shotId = id;
    this.onShot?.(id);
    const from = this.shot;
    return new Promise((resolve) => {
      this.shotTween = {
        at: (k) => this.blendShot(from, SHOTS[id], ease(k)),
        start: performance.now(),
        ms,
        done: resolve,
      };
    });
  }

  /**
   * Turns the camera around the mouth and back over `ms`: from the current shot to a little past
   * the three-quarter view (`reach` of the way on toward the front one) and back, so the tongue's
   * shape is seen from several angles; ends on the shot it started from.
   */
  tour(ms = 6000, reach = 0.15): Promise<void> {
    this.shotTween?.done();
    this.userTurned = false;
    const from = this.shot;
    // Not far toward the front: past about a third of the way the lips and the braced tongue were
    // never shaped for (the teeth come through the upper lip, the tongue looks like a block).
    const peak = this.blendShot(SHOTS.threeQuarter, SHOTS.front, reach);
    return new Promise((resolve) => {
      this.shotTween = {
        // sin^2: starts and ends at rest, the turn fastest midway.
        at: (k) => this.blendShot(from, peak, Math.sin(Math.PI * k) ** 2),
        start: performance.now(),
        ms,
        done: resolve,
      };
    });
  }

  /**
   * Puts the camera at once between the side view (0) and the three-quarter view (1), for
   * scripted demonstrations (debug Render, scripts/render-rw-video.mjs).
   */
  setTurn(k: number): void {
    this.shotTween?.done();
    this.shotTween = null;
    this.userTurned = false;
    this.applyShot(this.blendShot(SHOTS.side, SHOTS.threeQuarter, k));
  }

  /**
   * Moves the face and the lower teeth (mm: [forward, up]) against the rest of the mouth, for
   * fitting them to a reference (?align); omitted parts keep their offset.
   */
  setFit(fit: { face?: [number, number]; lower?: [number, number] }): void {
    this.fit = { face: fit.face ?? this.fit.face, lower: fit.lower ?? this.fit.lower };
    for (const mesh of this.head)
      mesh.position.set(0, this.fit.face[1] * MM, this.fit.face[0] * MM);
    for (const part of this.lowerParts)
      part.position.set(0, this.fit.lower[1] * MM, this.fit.lower[0] * MM);
  }

  /**
   * A camera set exactly (radians, metres, mm focus; `fov` in degrees, default 30), e.g. straight
   * from the side with a narrow field of view to compare with an X-ray (?align).
   */
  setView(view: {
    azimuth: number;
    elevation: number;
    distance: number;
    focus: [number, number, number];
    fov?: number;
  }): void {
    this.shotTween?.done();
    this.shotTween = null;
    this.userTurned = false;
    this.camera.fov = view.fov ?? 30;
    this.camera.updateProjectionMatrix();
    this.applyShot({ ...SHOTS.side, ...view });
  }

  /** Shows or hides the see-through face (e.g. to see the teeth against a reference). */
  setSkinVisible(visible: boolean): void {
    for (const mesh of this.head) mesh.visible = visible;
  }

  /** The canvas the scene draws on. */
  get canvas(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  /** Marks the narrowest point between the tongue and the roof (yellow; red when touching). */
  setGapMarker(on: boolean): void {
    this.showGap = on;
  }

  /** The tongue's narrowest gap to the roof (mm) for the current shape. */
  gap(): { mm: number; z: number } {
    return roofGap(this.shape.tongue);
  }

  /** Where a midline point (mm, [z, y]) is on screen (CSS px in the host). */
  toScreen(z: number, y: number): { x: number; y: number } {
    const p = new THREE.Vector3(0, y * MM, z * MM).project(this.camera);
    const { width, height } = this.renderer.domElement.getBoundingClientRect();
    return { x: ((p.x + 1) / 2) * width, y: ((1 - p.y) / 2) * height };
  }

  /** The midline point (mm, [z, y]) under a screen position (CSS px in the host). */
  fromScreen(x: number, y: number): { z: number; y: number } | null {
    const { width, height } = this.renderer.domElement.getBoundingClientRect();
    const ray = new THREE.Raycaster();
    ray.setFromCamera(new THREE.Vector2((x / width) * 2 - 1, 1 - (y / height) * 2), this.camera);
    const hit = new THREE.Vector3();
    if (!ray.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(1, 0, 0), 0), hit)) return null;
    return { z: hit.z / MM, y: hit.y / MM };
  }

  private applyShot(shot: Shot) {
    this.shot = shot;
    const focus = new THREE.Vector3(shot.focus[0], shot.focus[1], shot.focus[2]).multiplyScalar(MM);
    this.controls.target.copy(focus);
    if (!this.userTurned) {
      // On a stage narrower than the shots were framed for (a phone held upright), step back so
      // the mouth still fits across.
      const distance = shot.distance * Math.max(1, FRAMED_ASPECT / this.camera.aspect);
      this.camera.position.set(
        focus.x - Math.cos(shot.azimuth) * Math.cos(shot.elevation) * distance,
        focus.y + Math.sin(shot.elevation) * distance,
        focus.z + Math.sin(shot.azimuth) * Math.cos(shot.elevation) * distance,
      );
      this.camera.lookAt(focus);
    }
    for (const [mesh, opacity] of this.skinLayers)
      (mesh.material as THREE.MeshStandardMaterial).opacity = opacity * shot.skin;
    // The cutaway has no near half: the teeth and lining on the camera's side are gone with it.
    const near = this.style === 'cutaway' ? 0 : shot.near;
    this.nearFade.value = near;
    // The near gums and lining fainter than the teeth: the teeth are the reference for where the
    // tongue is; the lining in front of it would only veil it.
    this.nearGums.value = near * 0.6;
    this.nearLining.value = near * 0.2;
    for (const material of this.palateMaterials) {
      material.opacity = shot.palate;
      material.visible = shot.palate > 0.02;
    }
  }

  private blendShot(a: Shot, b: Shot, t: number): Shot {
    const mix = (x: number, y: number) => x + (y - x) * t;
    return {
      azimuth: mix(a.azimuth, b.azimuth),
      elevation: mix(a.elevation, b.elevation),
      distance: mix(a.distance, b.distance),
      focus: [
        mix(a.focus[0], b.focus[0]),
        mix(a.focus[1], b.focus[1]),
        mix(a.focus[2], b.focus[2]),
      ],
      skin: mix(a.skin, b.skin),
      palate: mix(a.palate, b.palate),
      near: mix(a.near, b.near),
    };
  }

  private readonly frame = (now: number) => {
    if (this.disposed) return;
    if (this.shapeTween) {
      const k = Math.min(1, (now - this.shapeTween.start) / this.shapeTween.ms);
      this.shape = blendShapes(this.shapeTween.from, this.shapeTween.to, ease(k));
      if (k >= 1) {
        const done = this.shapeTween.done;
        this.shapeTween = null;
        done();
      }
    }
    if (this.shotTween) {
      const k = Math.min(1, (now - this.shotTween.start) / this.shotTween.ms);
      this.applyShot(this.shotTween.at(k));
      if (k >= 1) {
        const done = this.shotTween.done;
        this.shotTween = null;
        done();
      }
    } else if (!this.userTurned) this.applyShot(this.shot);
    this.pose();
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
    if (this.visible) this.raf = requestAnimationFrame(this.frame);
  };

  private pose() {
    const shape = this.shape;
    if (this.tongueData) {
      poseTongue(
        this.tongueData.binding,
        {
          ...shape.tongue,
          floor: FLOOR_OF_MOUTH,
          ...(shape.tongue.bracing
            ? { brace: { weights: shape.tongue.bracing, palate: palateSection } }
            : {}),
        },
        this.posed,
        this.tongueData.faces,
      );
      applyStencil(this.tongueData.fine, this.posed, this.drawn);
      // Bending leaves creases (most where the sides brace, which made the r look boxy): Taubin
      // passes round them off without shrinking the tongue.
      taubinSmooth(
        this.drawn,
        this.tongueData.neighbours,
        TONGUE_SMOOTHING_PASSES,
        0.5,
        -0.53,
        this.smoothScratch,
      );
      this.tongueGeometry.getAttribute('position').needsUpdate = true;
      for (let v = 0; v < this.drawn.length / 3; v++) {
        const y = this.drawn[v * 3 + 1] ?? 0;
        const k = Math.min(
          1,
          Math.max(0, (y - TONGUE_FADE[0]) / (TONGUE_FADE[1] - TONGUE_FADE[0])),
        );
        this.tongueFade[v * 4 + 3] = k * k * (3 - 2 * k);
      }
      this.tongueGeometry.getAttribute('color').needsUpdate = true;
      this.tongueGeometry.computeVertexNormals();
      this.tongueGeometry.computeBoundingSphere();
    }
    const pivot = new THREE.Vector3(JAW_PIVOT[0], JAW_PIVOT[1], JAW_PIVOT[2]).multiplyScalar(MM);
    this.jaw.position
      .copy(pivot)
      .negate()
      .applyAxisAngle(new THREE.Vector3(1, 0, 0), (shape.jaw * Math.PI) / 180)
      .add(pivot);
    this.jaw.rotation.x = (shape.jaw * Math.PI) / 180;
    const weights = mouthWeights(shape);
    for (const part of this.head) {
      const dict = part.morphTargetDictionary ?? {};
      const influences = part.morphTargetInfluences ?? [];
      for (const [name, w] of Object.entries(weights)) {
        const i = dict[name];
        if (i !== undefined) influences[i] = w;
      }
    }
    if (this.showGap) {
      const { mm, z } = roofGap(shape.tongue);
      const roof = roofHeight(z) ?? 0;
      this.glow.visible = true;
      this.glow.position.set(0, (roof - Math.max(0, mm) / 2) * MM, z * MM);
      (this.glow.material as THREE.MeshBasicMaterial).color.setHex(
        mm < 1 ? COLORS.touch : COLORS.highlight,
      );
    } else this.glow.visible = false;
  }

  private resize() {
    const { width, height } = this.host.getBoundingClientRect();
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
    if (!this.shotTween) this.applyShot(this.shot);
  }

  dispose(): void {
    this.disposed = true;
    this.visible = false;
    cancelAnimationFrame(this.raf);
    this.shapeTween?.done();
    this.shotTween?.done();
    this.observer.disconnect();
    this.sizes.disconnect();
    this.controls.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
